import { Info } from 'lucide-react';
import WaterfallChart from './charts/WaterfallChart';
import WeekDetailTable from './WeekDetailTable';
import { formatMoney } from './charts/IbcsTooltip';
import type { ForecastData } from '../../types/cashCockpit';

interface Props {
    data: ForecastData;
    /** Entities currently in scope — enables the Week Detail drill-down modal. */
    entities?: string[];
    /** Currency mode passed to the breakdown endpoint ('ALL' or ISO code). */
    currencyMode?: string;
}

export default function ForecastTab({ data, entities, currencyMode }: Props) {
    const fc = data.forecast;
    if (fc.length === 0) {
        return <div className="text-gray-500 text-sm text-center py-8">No forecast data.</div>;
    }
    const ccy = data.displayCurrency || 'EUR';
    const first = fc[0];
    const last = fc[fc.length - 1];
    const isLimited = first.isLimitedForecast;
    const totalInflow = fc.reduce((s, w) => s + w.arInflowEUR + w.plannedInflowEUR, 0);
    const totalOutflow = fc.reduce((s, w) => s + w.apOutflowEUR + w.plannedOutflowEUR, 0);
    const anyFlags = Array.from(new Set(fc.flatMap(w => w.flags ?? [])));

    return (
        <div className="space-y-4">
            {isLimited && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Info className="w-4 h-4" />
                    Limited forecast: selection includes manual-entry companies without AR/AP automation.
                </div>
            )}

            {anyFlags.length > 0 && !isLimited && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Info className="w-4 h-4 mt-0.5" />
                    <ul className="list-disc ml-4 space-y-0.5">
                        {anyFlags.map(f => <li key={f}>{f}</li>)}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-4 gap-4">
                <Kpi label="Opening" value={first.openingCashEUR} hint={first.weekStart} currency={ccy} />
                <Kpi label={`Total Inflows (13W)`} value={totalInflow} semantic="positive" currency={ccy} />
                <Kpi label={`Total Outflows (13W)`} value={totalOutflow} semantic="negative" currency={ccy} />
                <Kpi label="Closing (W13)" value={last.closingCashEUR} hint={last.weekEnd} currency={ccy} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">13-Week Cash Forecast ({ccy})</h3>
                <WaterfallChart data={fc} height={320} displayCurrency={ccy} />
            </div>

            <WeekDetailTable
                forecast={fc}
                displayCurrency={ccy}
                entities={entities}
                currencyMode={currencyMode ?? ccy}
            />
        </div>
    );
}

function Kpi({ label, value, hint, semantic, currency = 'EUR' }: { label: string; value: number; hint?: string; semantic?: 'positive' | 'negative'; currency?: string }) {
    const color = semantic === 'positive' ? 'text-[#2C7BB6]' : semantic === 'negative' ? 'text-[#D7191C]' : 'text-gray-900';
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`text-xl font-semibold font-mono tabular-nums ${color}`}>{formatMoney(value, currency)}</div>
            {hint && <div className="text-xs text-gray-400">{hint}</div>}
        </div>
    );
}
